import { ScreenErrorBoundary, type ScreenErrorFallbackProps } from '@fuze/portal-core';
import { useQueryErrorResetBoundary } from '@tanstack/react-query';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7';
import { Outlet } from 'react-router';

import { useReporter } from '@/app/observability/ReportingContext';
import { errorCopy } from '@/content';

/**
 * Placeholder until Nexus error-state components land. Generic copy only: the raw error message is for the
 * reporter, never the user. The reference id is shown so support can find the event.
 */
const ScreenErrorFallback = ({ referenceId, reset }: ScreenErrorFallbackProps) => (
  <main>
    <h1>{errorCopy.unexpected.title}</h1>
    <p role="alert">{errorCopy.unexpected.body}</p>
    {referenceId !== undefined && (
      <p>
        {errorCopy.unexpected.referenceLabel}: {referenceId}
      </p>
    )}
    <button
      type="button"
      onClick={reset}
    >
      Try again
    </button>
  </main>
);

export const RootLayout = () => {
  const reporter = useReporter();
  // Queries that threw to this boundary stay failed until reset; "Try again" resets them so they refetch.
  const { reset: resetQueries } = useQueryErrorResetBoundary();
  return (
    <NuqsAdapter>
      <ScreenErrorBoundary
        fallback={(props) => (
          <ScreenErrorFallback
            {...props}
            reset={() => {
              resetQueries();
              props.reset();
            }}
          />
        )}
        report={(error, info) => reporter.report(error, { source: 'screen', componentStack: info.componentStack })}
      >
        <Outlet />
      </ScreenErrorBoundary>
    </NuqsAdapter>
  );
};
